import { X, MapPin, Clock, Calendar, Info, User as UserIcon, ClipboardCheck, Store, ChefHat, Users, SprayCan, GraduationCap, BookOpen, UserCog, Wrench, Handshake, FileDown } from "lucide-react";
import { PhotoStrip } from "@/components/PhotoUploader";
import { ACTIVITY_TYPES, RESULTAT_OPTS, SATISFACTION_OPTS } from "@/lib/activityTypes";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICONS = { Store, Handshake, SprayCan, GraduationCap, Users, ClipboardCheck, ChefHat, BookOpen, UserCog, Wrench };
const typeByKey = Object.fromEntries(ACTIVITY_TYPES.map(t => [t.key, t]));

const Row = ({ label, value }) => value == null || value === "" ? null : (
  <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4 py-2 border-b border-slate-100 last:border-0">
    <div className="text-xs font-semibold tracking-wider text-slate-500 uppercase sm:w-44 shrink-0">{label}</div>
    <div className="text-sm text-slate-800 flex-1 whitespace-pre-wrap">{value}</div>
  </div>
);

function SignatureBlock({ label, dataUrl }) {
  if (!dataUrl) return null;
  return (
    <div className="rounded-xl border border-slate-200 p-3 bg-slate-50">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{label}</div>
      <img src={dataUrl} alt="signature" className="max-h-32 bg-white rounded-md p-2 border border-slate-200"/>
    </div>
  );
}

function AuditItems({ p }) {
  const answers = p.answers || {};
  const entries = Object.entries(answers);
  if (entries.length === 0) return null;
  const grouped = { conforme: [], non_conforme: [], na: [] };
  entries.forEach(([label, val]) => (grouped[val] || grouped.na).push(label));
  const chip = { conforme: "bg-emerald-100 text-emerald-700", non_conforme: "bg-rose-100 text-rose-700", na: "bg-slate-100 text-slate-500" };
  const labels = { conforme: "Conforme", non_conforme: "Non conforme", na: "Non applicable" };
  return (
    <div className="space-y-3">
      {["non_conforme", "conforme", "na"].map(k => grouped[k].length > 0 && (
        <div key={k}>
          <div className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mb-2 ${chip[k]}`}>{labels[k]} ({grouped[k].length})</div>
          <ul className="space-y-1">
            {grouped[k].map((label, i) => (
              <li key={i} className="text-sm text-slate-700 flex gap-2">
                <span className="text-slate-400">{k === "conforme" ? "✓" : k === "non_conforme" ? "✕" : "•"}</span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function ActivityDetailModal({ activity, onClose, storeName, sectorName }) {
  if (!activity) return null;
  const meta = typeByKey[activity.type_key] || { color: "#3b82f6", icon: "Store", label: activity.visit_type };
  const Icon = ICONS[meta.icon] || Store;
  const p = activity.payload || {};

  const satisfLabel = SATISFACTION_OPTS.find(x => x.v === p.satisfaction)?.label || p.satisfaction;
  const resultatLabel = RESULTAT_OPTS.find(x => x.v === p.resultat)?.label || p.resultat;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm grid place-items-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-3xl border border-slate-200 shadow-xl my-4 max-h-[95vh] flex flex-col">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: `${meta.color}18`, color: meta.color }}>
            <Icon className="w-5 h-5"/>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg font-semibold">{meta.label}</h2>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3"/> {new Date(activity.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
              <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3"/> {activity.user_name}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3"/> {activity.duration_min} min</span>
            </div>
          </div>
          <button data-testid="ad-pdf" type="button" onClick={async () => {
              try {
                const res = await api.get(`/activities/${activity.id}/pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a"); a.href = url; a.download = `orbit_activite_${activity.id.slice(0,8)}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch { toast.error("Impossible de générer le PDF"); }
            }} title="Télécharger PDF" className="flex items-center gap-1.5 px-3 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">
            <FileDown className="w-4 h-4"/> PDF
          </button>
          <button data-testid="ad-close" type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto min-h-0 flex-1">
          {/* Store & sector */}
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 flex flex-wrap items-center gap-3 text-sm text-slate-700">
            {activity.store_id && <span className="inline-flex items-center gap-1.5 font-semibold"><MapPin className="w-4 h-4 text-slate-400"/> {storeName(activity.store_id)}</span>}
            {activity.sector_id && <span className="text-slate-500">· {sectorName(activity.sector_id)}</span>}
            {activity.info_important && (
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wider">
                <Info className="w-3 h-3"/> Important
              </span>
            )}
            {p.score_pct != null && (
              <span className={`ml-auto text-sm font-bold px-3 py-1 rounded-full ${p.score_pct >= 85 ? "bg-emerald-100 text-emerald-700" : p.score_pct >= 65 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                Conformité {p.score_pct}% ({p.items_ok}/{p.items_total})
              </span>
            )}
          </div>

          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
            <div className="p-4 space-y-0">
              <Row label="Objet" value={p.objet}/>
              <Row label="Interlocuteur" value={p.interlocuteur}/>
              <Row label="Nom / Prénom" value={p.nom_prenom}/>
              <Row label="Type de formation" value={p.formation_type}/>
              <Row label="Type d'entretien" value={p.type_entretien}/>
              <Row label="Lieu" value={p.lieu === "Autre" ? p.lieu_autre : p.lieu}/>
              <Row label="Conditions" value={p.condition ? (p.condition === "favorable" ? "Favorables" : `Défavorables${p.condition_precision ? ` — ${p.condition_precision}` : ""}`) : null}/>
              <Row label="Méthode / process" value={p.methode}/>
              <Row label="Action réalisée" value={p.action === "Autre" || p.action === "Petite réparation" ? `${p.action}${p.action_autre ? " — " + p.action_autre : ""}` : p.action}/>
              <Row label="Format" value={p.format === "en_ligne" ? "En ligne" : p.format === "papier" ? "Papier" : null}/>
              <Row label="Tenue remise" value={p.tenue_remise === true ? "Oui" : null}/>
              <Row label="Note" value={p.note != null ? `${p.note}${activity.type_key === "formation_hygiene" ? "/20" : activity.type_key === "formation_technique" ? "/10" : ""}` : null}/>
              <Row label="Détail" value={p.detail}/>
              <Row label="Déroulé" value={p.deroule}/>
              <Row label="Compte rendu" value={p.compte_rendu}/>
              <Row label="Intervention" value={p.intervention}/>
              <Row label="Commentaire" value={p.commentaire}/>
              <Row label="Signalement" value={p.signalement}/>
              <Row label="Avis salarié" value={p.avis_salarie}/>
              <Row label="Prochaine action" value={p.prochaine_action}/>
              <Row label="Besoin après intervention" value={p.besoin_apres}/>
              <Row label="Résultat" value={resultatLabel}/>
              <Row label="Satisfaction RDV" value={satisfLabel}/>
              <Row label="CR envoyé magasin" value={p.cr_envoye_magasin === true ? "Oui" : null}/>
              <Row label="CR envoyé N+1" value={p.cr_envoye_n1 === true ? "Oui" : null}/>
              <Row label="Résolu ?" value={p.resolu === true ? "Oui" : p.resolu === false ? "Non" : null}/>
              <Row label="Envoi N+1" value={p.envoi_n1 === true ? "Oui" : null}/>
              <Row label="Acquis" value={(p.acquis || []).join(" · ") || null}/>
              <Row label="Non acquis" value={(p.non_acquis || []).join(" · ") || null}/>
              <Row label="Prochaine visite" value={activity.next_visit_date ? new Date(activity.next_visit_date).toLocaleDateString("fr-FR") : null}/>
            </div>
          </div>

          {/* Audit items */}
          {activity.type_key === "audit_corner" && p.answers && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-semibold tracking-wider text-slate-500 uppercase mb-3">Détail audit — {p.sub_type === "hygiene" ? "Hygiène (Grille Wasabi)" : "Corner"}</div>
              <AuditItems p={p}/>
            </div>
          )}

          {/* Participants */}
          {(p.participants || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-semibold tracking-wider text-slate-500 uppercase mb-3">Participants</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {p.participants.map((pp, i) => (
                  <div key={i} className="rounded-lg border border-slate-100 p-3">
                    <div className="text-sm font-semibold text-slate-800">{pp.nom}</div>
                    {pp.signature && <img src={pp.signature} alt="sig" className="max-h-20 mt-2 bg-white rounded-md p-1 border border-slate-200"/>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Signature */}
          <SignatureBlock label="Signature" dataUrl={p.signature}/>

          {/* Photos */}
          {(activity.photos || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 p-4">
              <div className="text-xs font-semibold tracking-wider text-slate-500 uppercase mb-3">Photos & pièces jointes</div>
              <PhotoStrip paths={activity.photos}/>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
